import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimationController, type AnimationSnapshot } from '../../animation/animationController';
import type { AvSession, Project } from '../../domain/schemas';
import { debugCounters } from '../../lib/debug';
import { Observable } from '../../lib/store';
import { useObservable, useObservableSelector } from '../../lib/useObservable';
import { fitCamera } from '../../scene/geometry';
import { useServices } from '../../state/services';
import { Alert } from '../ui';
import { visualizerDigest } from '../../visualizer/digest';

/**
 * The synchronized preview (Phase 4A): a renderer that follows the session clock and draws
 * the session's scene, its animation (permanent keyframes) and the reactive overlays
 * (reactions, motion cues, effects). Read-only — scenes are edited in the Animation tab.
 * Rendered at device-pixel ratio 1 to stay light on modest hardware.
 */
export function SyncPreview({ project, session }: { project: Project; session: AvSession }) {
  const { sync, images, analysis, animation: bus } = useServices();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const controllerRef = useRef<AnimationController | null>(null);
  const [state] = useState(() => new Observable<AnimationSnapshot>({ status: 'idle', frame: 0, elapsed: 0 }));
  const status = useObservableSelector(state, (s) => s.status);
  const error = useObservableSelector(state, (s) => s.error);
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const animation = session.animationId ? (project.animations.find((a) => a.id === session.animationId) ?? null) : null;
  const latest = useRef({ scene, characters: project.characters, animation });
  useLayoutEffect(() => {
    latest.current = { scene, characters: project.characters, animation };
  });

  const fit = () => {
    const c = controllerRef.current;
    if (!c) return;
    const view = c.viewSize;
    if (view.width < 2) return;
    c.setCamera(fitCamera(latest.current.scene.width, latest.current.scene.height, view.width, view.height, 8));
  };
  const fitRef = useRef(fit);
  useLayoutEffect(() => {
    fitRef.current = fit;
  });

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const controller = new AnimationController(undefined, state, {
      loadImage: (id) => images.load(id),
      onResize: () => fitRef.current(),
      clock: sync.clock,
      maxResolution: 1,
      effectsQuality: bus.draftEffects.get() ? 'draft' : 'full',
    });
    controllerRef.current = controller;
    controller.setReactor((seconds) => sync.reactions(seconds));
    controller.setVisualizer((seconds) => sync.visualizer(seconds));
    debugCounters.syncState = () => {
      const fx = controller.shownEffects();
      const stats = controller.stats();
      const sample = sync.clock.sample();
      return {
        seconds: sample.seconds,
        shownSeconds: controller.shownPosition,
        frame: sample.frame,
        playing: sync.clock.get().playing,
        status: sync.state.get().status,
        source: sync.state.get().source,
        values: Object.fromEntries(controller.displayedValues()),
        effects: fx ? { flash: fx.flash?.alpha ?? 0, background: fx.background?.alpha ?? 0, shakeX: fx.shake.x, shakeY: fx.shake.y } : null,
        reactionEvaluations: stats.reactionEvaluations,
        reactionMs: stats.reactionMs,
        reactingObjects: stats.reactingObjects,
        visualizer: visualizerDigest(controller.shownVisualizer()),
      };
    };
    debugCounters.sceneStats = () => controller.stats();
    // Phase 5B.0 test hook: compiled out of the production build (see vite.config.ts).
    if (__SONICFORGE_TEST_HOOKS__) {
      debugCounters.syncSeek = (seconds) => {
        const t0 = performance.now();
        void sync.seek(seconds); // the UI's seek path (synchronous while paused)
        return performance.now() - t0;
      };
    }
    void controller.mount(stage, latest.current.scene, latest.current.characters).then(() => {
      if (controllerRef.current !== controller) return;
      controller.setAnimation(latest.current.animation);
      fitRef.current();
    });
    return () => {
      controller.destroy();
      if (controllerRef.current === controller) controllerRef.current = null;
      debugCounters.syncState = () => null;
      debugCounters.sceneStats = () => null;
      if (__SONICFORGE_TEST_HOOKS__) debugCounters.syncSeek = undefined;
    };
  }, [state, images, sync, bus]);

  // Phase 5B.1: the shared draft-effects preference (export is always full quality).
  const draftEffects = useObservable(bus.draftEffects);
  useEffect(() => {
    controllerRef.current?.setEffectsQuality(draftEffects ? 'draft' : 'full');
  }, [draftEffects]);

  useEffect(() => {
    controllerRef.current?.setScene(scene);
    fitRef.current();
  }, [scene]);
  useEffect(() => {
    controllerRef.current?.setCharacters(project.characters);
  }, [project.characters]);
  useEffect(() => {
    controllerRef.current?.setAnimation(animation);
  }, [animation, status]);
  // Mapping, cue, visualizer or timing edits while paused: show their effect at the playhead now.
  useEffect(() => {
    controllerRef.current?.refresh();
  }, [session, status]);
  // Measured audio analysis finished: redraw with it.
  useEffect(() => analysis.state.subscribe(() => controllerRef.current?.refresh()), [analysis]);

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={stageRef}
        className="relative aspect-video max-h-[24rem] w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
        role="img"
        aria-label={`Synchronized preview of “${scene.name}”${animation ? ` with “${animation.name}”` : ''}`}
        data-testid="sync-preview"
        data-status={status}
      />
      {status === 'error' && <Alert title="The preview is unavailable">{error}</Alert>}
    </div>
  );
}

